import { IProjectView, ProjectLifecycleFormType } from '../../api-client/autogenerated';
import {
  DocumentFileTree,
  DocumentFileTreeDocument,
  STORYTELLING_PROGRESS_TREE_TYPE,
  STORYTELLING_QC_TREE_TYPE,
  STORYTELLING_TREE_TYPE,
} from '../../scripts/utils';

/**
 * The project's storytelling forms as document trees, for the Share Anything card's Attach
 * Documents dialog. They are not documents, so they ride in trees of their own and each entry's
 * id is `<formType>:<formId or empty>` -- the pair the share sends back to mint that form's
 * public link.
 *
 * One tree per kind, so the two a project can have many of group under their own heading the way
 * documents of one type do. The three it only ever has one of share a tree whose type is the
 * phase's own name, which the dialog lists directly under the phase rather than under a heading
 * repeating it.
 *
 * Within a group the entries are named by date alone, since the heading already says what they
 * are; `userFriendlyName` keeps the full name for whoever lifts one out of the tree.
 *
 * Only the forms the project actually has are listed, so a project with none leaves the phase
 * greyed out like any other empty one.
 */
export const getStorytellingShareTrees = (project: IProjectView | null): DocumentFileTree[] => {
  const singleForms: DocumentFileTreeDocument[] = [];

  if (project?.projectRequest)
    singleForms.push({ id: 'request:', title: 'Project Lifecycle Form', checked: false });
  if (project?.projectCloseout)
    singleForms.push({ id: 'closeout:', title: 'Project Closeout Form', checked: false });
  if (project?.projectA3) singleForms.push({ id: 'a3:', title: 'A3 Summary', checked: false });

  const progressReports: DocumentFileTreeDocument[] = (project?.projectProgressReports ?? []).map(
    (report) => {
      const date = report.data?.reportDate;
      return {
        id: `progress:${report.id}`,
        title: date || 'Undated',
        userFriendlyName: `Project Progress Report${date ? ` - ${date}` : ''}`,
        checked: false,
      };
    },
  );

  const qualityControlForms: DocumentFileTreeDocument[] = (
    project?.projectQualityControlForms ?? []
  ).map((form) => {
    const date = form.data?.qcDate;
    return {
      id: `quality-control:${form.id}`,
      title: date || 'Undated',
      userFriendlyName: `Quality Control Form${date ? ` - ${date}` : ''}`,
      checked: false,
    };
  });

  // Empty trees are dropped by the dialog, so they are returned unconditionally.
  return [
    { type: STORYTELLING_TREE_TYPE, documents: singleForms },
    { type: STORYTELLING_PROGRESS_TREE_TYPE, documents: progressReports },
    { type: STORYTELLING_QC_TREE_TYPE, documents: qualityControlForms },
  ];
};

/** Splits a storytelling tree entry's id back into the form type and, where there is one, its id. */
export const parseStorytellingFormId = (id: string) => {
  const [formType, formId] = id.split(':');
  return { formType: formType as ProjectLifecycleFormType, formId: formId || undefined };
};
